import React, { useState, useEffect } from "react";
import styles from "./Login.module.scss";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { LOGIN_POST } from "../../../services/const";
import logo from "./logo.png";

export default function Login() {
  const navigate = useNavigate();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const rememberedUserName = localStorage.getItem("rememberedUserName");
    const rememberedPassword = localStorage.getItem("rememberedPassword");
    if (rememberedUserName && rememberedPassword) {
      setUserName(rememberedUserName);
      setPassword(rememberedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    const errs = {};
    if (!userName.trim()) {
      errs.userName = "Username or email is required";
    }
    if (!password) {
      errs.password = "Password is required";
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setIsLoading(true);

    if (rememberMe) {
      localStorage.setItem("rememberedUserName", userName);
      localStorage.setItem("rememberedPassword", password);
    } else {
      localStorage.removeItem("rememberedUserName");
      localStorage.removeItem("rememberedPassword");
    }

    const obj = {
      username: userName,
      password: password,
    };

    const apiUrl = new URL(LOGIN_POST);
    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(obj),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsLoading(false);
        if (data && "content" in data && data.code === 200) {
          sessionStorage.setItem("bb_token", data.content.token);
          sessionStorage.setItem("bb_userId", data.content.userId);
          sessionStorage.setItem("bb_role", data.content.role);
          sessionStorage.setItem("bb_fullName", data.content.fullName);
          if (data.content.role === "admin") navigate("/home");
          else navigate("/table");
        } else {
          if (data && "message" in data) {
            setErrors({ form: data.message });
          } else {
            setErrors({ form: "Invalid credentials. Please try again." });
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Exception Login API: ", error);
        setErrors({ form: "Unable to reach server. Please check your connection." });
      });
  };

  const onKeyDownHandler = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleSubmitForm(e);
    }
  };

  return (
    <div className={styles.loginWrapper}>
      <div className={styles.loginCard}>
        <div className={styles.logoHeader}>
          <img src={logo} alt="Oktobiz" className={styles.logoImg} />
          <h1 className={styles.title}>Admin Workspace</h1>
          <p className={styles.subtitle}>Sign in with your administrator credentials</p>
        </div>

        <form onSubmit={handleSubmitForm} className={styles.form}>
          {errors.form && (
            <div className={styles.errorAlert}>
              <i className="fa-solid fa-circle-exclamation"></i>
              <span>{errors.form}</span>
            </div>
          )}

          <div className={styles.fieldGroup}>
            <FormControl variant="outlined" fullWidth error={!!errors.userName}>
              <InputLabel htmlFor="username-input">Username</InputLabel>
              <OutlinedInput
                id="username-input"
                type="text"
                label="Username"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyDown={onKeyDownHandler}
              />
            </FormControl>
            {errors.userName && <p className={styles.fieldError}>{errors.userName}</p>}
          </div>

          <div className={styles.fieldGroup}>
            <FormControl variant="outlined" fullWidth error={!!errors.password}>
              <InputLabel htmlFor="password-input">Password</InputLabel>
              <OutlinedInput
                id="password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={onKeyDownHandler}
              />
            </FormControl>
            {errors.password && <p className={styles.fieldError}>{errors.password}</p>}
          </div>

          <div className={styles.optionsRow}>
            <label className={styles.rememberCheckbox}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <Link to="/forgetpassword" className={styles.forgotLink}>
              Forgot Password?
            </Link>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={isLoading}>
            {isLoading ? (
              <span>Signing in...</span>
            ) : (
              <>
                <span>Sign In</span>
                <i className="fa-solid fa-arrow-right"></i>
              </>
            )}
          </button>
        </form>

        <div className={styles.footerNote}>
          <i className="fa-solid fa-shield-halved"></i>
          <span>Secure 256-bit SSL Encrypted Admin Portal</span>
        </div>
      </div>
    </div>
  );
}

